import { useState, useEffect } from 'react';
import { Cookie, X, Check, Settings } from 'lucide-react';

const CONSENT_KEY = 'carehire-cookie-consent';

type Consent = {
  essential: true;
  analytics: boolean;
  preferences: boolean;
  timestamp: string;
} | null;

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [preferences, setPreferences] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  function saveConsent(consent: { analytics: boolean; preferences: boolean }) {
    const data = {
      essential: true as const,
      analytics: consent.analytics,
      preferences: consent.preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(data));
    setVisible(false);
    setShowSettings(false);
  }

  function dismiss() {
    saveConsent({ analytics, preferences });
  }

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
        {!showSettings ? (
          <div className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-ink-100 rounded-md flex items-center justify-center flex-shrink-0">
                <Cookie className="w-5 h-5 text-ink-900" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-gray-900 mb-1">We use cookies</h3>
                <p className="text-xs text-gray-500 leading-relaxed mb-4">
                  We use essential cookies to make CareHire work, and optional cookies for analytics and preferences.
                  See our <button onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'cookies' }))} className="text-ink-900 hover:underline">Cookie Policy</button> for details.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => saveConsent({ analytics: true, preferences: true })}
                    className="px-4 py-2 bg-ink-900 hover:bg-ink-800 text-white text-xs font-semibold rounded-md transition-colors"
                  >
                    Accept all
                  </button>
                  <button
                    onClick={() => saveConsent({ analytics: false, preferences: false })}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-md transition-colors"
                  >
                    Essential only
                  </button>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="px-4 py-2 text-gray-500 hover:text-gray-700 text-xs font-semibold rounded-md transition-colors flex items-center gap-1.5"
                  >
                    <Settings className="w-3.5 h-3.5" />
                    Preferences
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900">Cookie preferences</h3>
              <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Essential</p>
                  <p className="text-xs text-gray-500">Required for the platform to function. Cannot be disabled.</p>
                </div>
                <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Always on</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Analytics</p>
                  <p className="text-xs text-gray-500">Help us understand how the platform is used so we can improve it.</p>
                </div>
                <button
                  onClick={() => setAnalytics(!analytics)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${analytics ? 'bg-ink-900' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${analytics ? 'translate-x-5' : ''}`} />
                </button>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Preferences</p>
                  <p className="text-xs text-gray-500">Remember your settings such as language and theme.</p>
                </div>
                <button
                  onClick={() => setPreferences(!preferences)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${preferences ? 'bg-ink-900' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${preferences ? 'translate-x-5' : ''}`} />
                </button>
              </div>
            </div>
            <button
              onClick={dismiss}
              className="w-full py-2.5 bg-ink-900 hover:bg-ink-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center justify-center gap-2"
            >
              <Check className="w-4 h-4" />
              Save preferences
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
